import React, { useState } from 'react';
import { Settings, ChevronDown, ChevronUp, Lock } from 'lucide-react';
import { ALL_COLUMNS, MANDATORY_IDS, DEFAULT_PROFILES } from '../../config/tableColumns';

/**
 * Collapsible panel for selecting which columns appear in the PDF line items table.
 * Shows locked mandatory columns and toggleable optional columns.
 *
 * Props:
 *   selectedColumns - array of column IDs currently selected
 *   autoHideEmpty   - boolean toggle
 *   docType         - 'quotation' | 'order'
 *   onChange        - (selectedColumns, autoHideEmpty) => void
 */
export function PdfColumnSelector({ selectedColumns, autoHideEmpty, docType, onChange }) {
  const [open, setOpen] = useState(false);
  const availableColumns = ALL_COLUMNS.filter(c => !c.docTypes || c.docTypes.includes(docType));
  const selected = new Set(selectedColumns || DEFAULT_PROFILES[docType] || MANDATORY_IDS);
  const hideEmpty = autoHideEmpty !== false; // default true

  const toggle = (colId) => {
    if (MANDATORY_IDS.includes(colId)) return;
    const next = new Set(selected);
    if (next.has(colId)) next.delete(colId); else next.add(colId);
    // Preserve ordering from ALL_COLUMNS
    const ordered = ALL_COLUMNS.map(c => c.id).filter(id => next.has(id));
    onChange(ordered, hideEmpty);
  };

  const toggleAutoHide = () => {
    onChange([...selected], !hideEmpty);
  };

  const resetDefaults = () => {
    onChange([...(DEFAULT_PROFILES[docType] || MANDATORY_IDS)], true);
  };

  return (
    <div className="border rounded-lg bg-slate-50" data-testid="pdf-column-selector">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
        data-testid="pdf-columns-toggle"
      >
        <span className="flex items-center gap-2">
          <Settings className="h-4 w-4" />
          PDF Table Columns ({selected.size} of {availableColumns.length})
        </span>
        {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1 space-y-3">
          {/* Auto-hide toggle */}
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={hideEmpty}
              onChange={toggleAutoHide}
              className="rounded border-slate-300"
              data-testid="auto-hide-empty-toggle"
            />
            <span className="text-slate-600">Auto-hide empty columns</span>
          </label>

          {/* Column checkboxes */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {availableColumns.map(col => {
              const isMandatory = MANDATORY_IDS.includes(col.id);
              const isChecked = selected.has(col.id);
              return (
                <label
                  key={col.id}
                  className={`flex items-center gap-2 text-sm py-1 px-2 rounded ${
                    isMandatory ? 'text-slate-400 cursor-not-allowed' : 'text-slate-700 cursor-pointer hover:bg-white'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    disabled={isMandatory}
                    onChange={() => toggle(col.id)}
                    className="rounded border-slate-300"
                    data-testid={`col-toggle-${col.id}`}
                  />
                  <span>{col.label}</span>
                  {isMandatory && <Lock className="h-3 w-3 text-slate-400" />}
                </label>
              );
            })}
          </div>

          {/* Reset button */}
          <button
            type="button"
            onClick={resetDefaults}
            className="text-xs text-blue-600 hover:underline"
            data-testid="reset-columns-btn"
          >
            Reset to defaults
          </button>
        </div>
      )}
    </div>
  );
}
